/**
 * Address formatting utilities for consistent address display across the application.
 */

interface AddressFields {
  street?: string;
  street1?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

/**
 * Returns the address as a single line string.
 * Example: "246 Logan Avenue, Suite 306, Toronto, ON, M4M 2N3"
 *
 * @param address - An object containing address fields (Office or similar)
 * @param includeCountry - Whether to include country in the output (default: false)
 * @returns Single line address string
 */
export const formatAddressLine = (address: AddressFields | null | undefined, includeCountry = false): string => {
  if (!address) return "";

  const parts = [address.street, address.street1, address.city, address.state, address.zip, ...(includeCountry ? [address.country] : [])].filter(
    Boolean
  );

  return parts.join(", ");
};

/**
 * Returns the address formatted with line breaks.
 * Example:
 *   "246 Logan Avenue
 *    Suite 306
 *    Toronto, ON, M4M 2N3"
 *
 * @param address - An object containing address fields (Office or similar)
 * @param includeCountry - Whether to include country in the output (default: false)
 * @returns Multi-line address string with \n separators
 */
export const formatAddressMultiLine = (address: AddressFields | null | undefined, includeCountry = false): string => {
  if (!address) return "";

  const lines: string[] = [];

  // Line 1: Street
  if (address.street) {
    lines.push(address.street);
  }

  // Line 2: Suite/Unit (street1) - optional
  if (address.street1) {
    lines.push(address.street1);
  }

  // Line 3: City, State, Zip (and optionally Country)
  const cityStateZip = [address.city, address.state, address.zip, ...(includeCountry ? [address.country] : [])].filter(Boolean).join(", ");

  if (cityStateZip) {
    lines.push(cityStateZip);
  }

  return lines.join("\n");
};

/**
 * Returns address parts as an array of lines for JSX rendering.
 * Useful when you need to render each line as a separate element.
 *
 * @param address - An object containing address fields (Office or similar)
 * @param includeCountry - Whether to include country in the output (default: false)
 * @returns Array of address lines
 */
export const getAddressLines = (address: AddressFields | null | undefined, includeCountry = false): string[] => {
  if (!address) return [];

  const lines: string[] = [];

  // Line 1: Street
  if (address.street) {
    lines.push(address.street);
  }

  // Line 2: Suite/Unit (street1) - optional
  if (address.street1) {
    lines.push(address.street1);
  }

  // Line 3: City, State, Zip (and optionally Country)
  const cityStateZip = [address.city, address.state, address.zip, ...(includeCountry ? [address.country] : [])].filter(Boolean).join(", ");

  if (cityStateZip) {
    lines.push(cityStateZip);
  }

  return lines;
};
